'use strict';

import { dwService } from 'amb/services/dw.service';
import { tabIdentity } from '../utils/tabIdentity';
import { tabPresence } from '../coordinator/tabPresence';

/**
 * Options for {@link checkoutOwnership.acquireCheckout}.
 *
 * @typedef {Object} CheckoutOwnershipOptions
 *
 * @property {string} [checkoutTab] expected owner when force-replacing
 * @property {boolean} [forceReplace]
 */

/**
 * Checkout ownership protocol (multi-cart tab isolation).
 *
 * **Scope:**
 * - `acquireCheckout` — POST acquire; success → lease + navigate Begin
 * - Lease heartbeat (`startCheckoutLease` / `touchCheckoutLease`)
 * - `collectActiveCheckoutTabs` — presence + lease map for acquire payload
 *
 * Errors from acquire are HTTP 409 → global `ajaxHandlers` (not this module’s success `.then`).
 */
export const checkoutOwnership = {
  /**
   * localStorage key for the Checkout-Begin lease map (`{ [tabId]: until }`).
   *
   * @private
   * @readonly
   *
   * @type {string}
   */
  CHECKOUT_LEASE_KEY: 'mcCheckoutLease',

  /**
   * Lease TTL (ms). Longer than a presence probe gap; short after crash.
   *
   * @private
   * @readonly
   *
   * @type {number}
   */
  CHECKOUT_LEASE_TTL: 10000,

  /**
   * Heartbeat interval (ms) while this Checkout-Begin tab is open.
   *
   * @private
   * @readonly
   *
   * @type {number}
   */
  CHECKOUT_LEASE_INTERVAL: 3000,

  /**
   * Interval id for lease heartbeat, or null when not started.
   *
   * @private
   *
   * @type {number|null}
   */
  checkoutLeaseTimer: null,

  /**
   * POST `Tab-AcquireCheckout`. Success (HTTP 200) → lease + Begin.
   * Errors are HTTP 409 → global `ajaxHandlers` (not this `.then`).
   *
   * @param {CheckoutOwnershipOptions} [options]
   *
   * @returns {Promise<*>}
   */
  acquireCheckout(options = {}) {
    $.spinner().start();

    return this
      .collectActiveCheckoutTabs()
      .then(activeCheckoutTabs => $.ajax({
        url: dwService.url('Tab-AcquireCheckout'),
        method: 'post',
        dataType: 'json',
        data: {
          mcTab: tabIdentity.peekTabId(),
          activeCheckoutTabs: JSON.stringify(activeCheckoutTabs),
          expectedCheckoutTab: options.checkoutTab || '',
          forceReplace: options.forceReplace === true
        }
      }))
      .then(response => {
        this.touchCheckoutLease();

        window.location.assign(response.redirectUrl);
      })
      .finally(() => $.spinner().stop())
    ;
  },

  /**
   * Boot Checkout-Begin lease heartbeat. Idempotent.
   */
  startCheckoutLease() {
    this.touchCheckoutLease();

    if (this.checkoutLeaseTimer) {
      return;
    }

    this.checkoutLeaseTimer = window.setInterval(() => {
      this.touchCheckoutLease();
    }, this.CHECKOUT_LEASE_INTERVAL);
  },

  /**
   * Checkout-Begin tab ids from presence contexts, merged with lease map
   * entries when that tab misses the probe. Ignores a stale entry when the
   * tabId answered and is no longer on Checkout-Begin.
   *
   * @private
   *
   * @param {number} [timeout=400]
   *
   * @returns {Promise<string[]>}
   */
  collectActiveCheckoutTabs(timeout = 400) {
    return tabPresence
      .collectTabContexts(timeout)
      .then(contexts => {
        const byId = {};

        contexts.forEach(context => {
          byId[context.tabId] = context;
        });

        this.getCheckoutLeases().forEach(lease => {
          const live = byId[lease.tabId];

          // Probe miss → apply lease. Peer answered off checkout → ignore.
          // Already Checkout-Begin → presence is enough (no-op).
          if (!live) {
            byId[lease.tabId] = {
              tabId: lease.tabId,
              action: 'Checkout-Begin',
              params: { mcTab: lease.tabId }
            };
          }
        });

        return Object.keys(byId)
          .map(tabId => byId[tabId])
          .filter(context => context.action === 'Checkout-Begin')
          .map(context => context.tabId)
        ;
      })
    ;
  },

  /**
   * Upsert this tab's lease entry (`until = now + TTL`) and persist the map.
   * Prunes expired entries via `readLeaseMap` first.
   *
   * @private
   */
  touchCheckoutLease() {
    const tabId = tabIdentity.peekTabId();

    if (!tabId) {
      return;
    }

    const map = this.readLeaseMap();

    map[tabId] = Date.now() + this.CHECKOUT_LEASE_TTL;
    this.writeLeaseMap(map);
  },

  /**
   * Unexpired Checkout-Begin leases (`{ tabId, until }[]`).
   * Persists a pruned map (no upsert of the current tab).
   *
   * @private
   *
   * @returns {Array<{ tabId: string, until: number }>}
   */
  getCheckoutLeases() {
    const map = this.readLeaseMap();

    this.writeLeaseMap(map);

    return Object.keys(map).map(tabId => ({
      tabId: tabId,
      until: map[tabId]
    }));
  },

  /**
   * Read lease map and drop expired entries (in memory only).
   *
   * @private
   *
   * @returns {Record<string, number>}
   */
  readLeaseMap() {
    try {
      const raw = localStorage.getItem(this.CHECKOUT_LEASE_KEY);

      if (!raw) {
        return {};
      }

      const parsed = JSON.parse(raw);

      if (!parsed || typeof parsed !== 'object') {
        return {};
      }

      const now = Date.now();
      const map = {};

      Object.keys(parsed).forEach(tabId => {
        const until = parsed[tabId];

        if (tabId && typeof until === 'number' && until > now) {
          map[tabId] = until;
        }
      });

      return map;
    } catch (e) {
      return {};
    }
  },

  /**
   * Persist lease map, or remove the key when empty.
   *
   * @private
   *
   * @param {Record<string, number>} map
   */
  writeLeaseMap(map) {
    try {
      if (!map || !Object.keys(map).length) {
        localStorage.removeItem(this.CHECKOUT_LEASE_KEY);

        return;
      }

      localStorage.setItem(this.CHECKOUT_LEASE_KEY, JSON.stringify(map));
    } catch (e) {
      // Best-effort when storage is unavailable.
    }
  }
};
